<template>
  <Teleport to="body">
    <div v-if="visible" class="modal-overlay" @click.self="handleClose">
      <div class="modal-content">
        <div class="modal-header">
          <h3>{{ s__('Attachments|Supported File Types') }}</h3>
          <button type="button" class="modal-close" @click="handleClose" aria-label="Close">
            <CloseIcon />
          </button>
        </div>

        <div class="modal-body">
          <div class="info-section">
            <p>
              {{
                s__(
                  'Attachments|The following file types are supported for upload. Maximum file size is 100 MB.'
                )
              }}
            </p>

            <h4>{{ s__('Attachments|Allowed File Types') }}</h4>

            <div class="file-types-list">
              <div class="file-type-category">
                <h5>{{ s__('Attachments|Images') }}</h5>
                <ul>
                  <li>JPEG (.jpg, .jpeg)</li>
                  <li>PNG (.png)</li>
                  <li>GIF (.gif)</li>
                  <li>WebP (.webp)</li>
                  <li>SVG (.svg)</li>
                </ul>
              </div>

              <div class="file-type-category">
                <h5>{{ s__('Attachments|Videos') }}</h5>
                <ul>
                  <li>MP4 (.mp4)</li>
                  <li>WebM (.webm)</li>
                  <li>QuickTime (.mov)</li>
                  <li>AVI (.avi)</li>
                  <li>MKV (.mkv)</li>
                </ul>
              </div>

              <div class="file-type-category">
                <h5>{{ s__('Attachments|Audio') }}</h5>
                <ul>
                  <li>MP3 (.mp3)</li>
                  <li>OGG (.ogg)</li>
                  <li>WebM Audio (.webm)</li>
                  <li>WAV (.wav)</li>
                </ul>
              </div>

              <div class="file-type-category">
                <h5>{{ s__('Attachments|Documents') }}</h5>
                <ul>
                  <li>PDF (.pdf)</li>
                  <li>Microsoft Word (.doc, .docx)</li>
                  <li>Microsoft Excel (.xls, .xlsx)</li>
                  <li>Microsoft PowerPoint (.ppt, .pptx)</li>
                  <li>EPUB (.epub)</li>
                  <li>Apple Keynote (.key)</li>
                  <li>Apple Pages (.pages)</li>
                  <li>Apple Numbers (.numbers)</li>
                </ul>
              </div>

              <div class="file-type-category">
                <h5>{{ s__('Attachments|Text Files') }}</h5>
                <ul>
                  <li>Plain Text (.txt)</li>
                  <li>CSV (.csv)</li>
                  <li>Markdown (.md)</li>
                </ul>
              </div>

              <div class="file-type-category">
                <h5>{{ s__('Attachments|Archives') }}</h5>
                <ul>
                  <li>ZIP (.zip)</li>
                  <li>RAR (.rar)</li>
                  <li>7Z (.7z)</li>
                  <li>TAR (.tar)</li>
                  <li>GZIP (.gz)</li>
                </ul>
              </div>
            </div>

            <h4>{{ s__('Attachments|Blocked File Types') }}</h4>
            <p>
              {{
                s__('Attachments|The following file extensions are blocked for security reasons:')
              }}
            </p>
            <div class="blocked-extensions">
              <code
                v-for="(ext, index) in blockedExtensions"
                :key="index"
                class="blocked-extension"
              >
                {{ ext }}
              </code>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn-primary" @click="handleClose">
            {{ s__('Attachments|Got it') }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { computed } from 'vue'
import { Teleport } from 'vue'
import { s__ } from '@/locale'
import CloseIcon from './icons/CloseIcon.vue'
import { useBodyScrollLock } from '@/composables/useBodyScrollLock'

const props = defineProps({
  visible: {
    type: Boolean,
    required: true,
  },
})

const emit = defineEmits(['close'])

// Lock body scroll when modal is open
useBodyScrollLock(computed(() => props.visible))

const blockedExtensions = ['.exe', '.bat', '.cmd', '.com', '.pif', '.scr', '.vbs', '.js', '.jar']

function handleClose() {
  emit('close')
}
</script>

<style scoped>
.info-section {
  line-height: 1.6;
}

.info-section h4 {
  margin: 24px 0 12px 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.info-section h4:first-child {
  margin-top: 0;
}

.info-section p {
  margin: 0 0 16px 0;
  color: #666;
}

.file-types-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin: 16px 0;
}

.file-type-category {
  background: #f9fafb;
  border-radius: 8px;
  padding: 12px 16px;
}

.file-type-category h5 {
  margin: 0 0 8px 0;
  font-size: 14px;
  font-weight: 600;
  color: #111827;
}

.file-type-category ul {
  margin: 0;
  padding-left: 20px;
  color: #4b5563;
  font-size: 14px;
  line-height: 1.8;
}

.file-type-category li {
  margin-bottom: 4px;
}

.blocked-extensions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
}

.blocked-extension {
  background: #fee2e2;
  color: #991b1b;
  padding: 4px 8px;
  border-radius: 4px;
  font-family: monospace;
  font-size: 13px;
  font-weight: 500;
}
</style>
